import { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import BackButton from '../../Shared/BackButton'
import {months_list} from "../../Shared/List";
import { FaArrowUp, FaLock, FaLockOpen, FaFileExport } from "react-icons/fa"

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const UnBilledDetail = () => {
    const [unBilled, setUnBilled] = useState([])
    const [title, setTitle] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isLocking, setIsLocking] = useState(false)
    const [show, setShow] = useState(false);
    const [data, setData] = useState('')

    const gridRef = useRef();
    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {

        if (location.state){
            const {payload} = location.state
            fetchData({
                year: payload.year,
                month: payload.month,
                by: payload.by,
                public_id: payload.public_id
            })
            setTitle(`${payload.name}, ${months_list[payload.month - 1].name} - ${payload.year} UnBilled Customers`)

        }

    }, [])

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`un-billed/${payload.by}/?public_id=${payload.public_id}&year=${payload.year}&month=${payload.month}`)
            setUnBilled(response?.data)
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to fetch unbilled details')
            }
        }
    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    const gridApiRef = useRef(null);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    }
    const onExportClick = () => {
        if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv({ fileName: `${title}.csv` });
        } else {
            console.error("Grid API is not available yet!");
        }
    }

    const handleClose = () => {
        if (isLocking) return;
        setShow(false)
    }
    const handleShow = (data) => {
        setShow(true)
        setData(data)
    };

    const handleLock = async (data) => {
        setIsLocking(true)
        try {
            const response = await Server.post(`/un-billed/${data.public_id}/lock`)
            if (response.status === 201){
                const payload = {
                    year: data.year,
                    month: data.month,
                    public_id: data.public_id2,
                    by: data.by,
                    name: data.name,
                }
                navigate(`/un-billed-${data.by}`, { state: {data: payload}})
                navigate(0)
            }

        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to lock record')
            }
        } finally {
            setIsLocking(false)
            setIsLoading(false)
            setShow(false)
        }
    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
                if (isNaN(params.node.id)) {
                    return "";
                }
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "PID",
            field: "previous_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "ID",
            field: "location_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Name",
            field: "full_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Remark",
            field: "remark",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Contact",
            field: "contact_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 120,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                const lockPayload = {
                    year: e.data.year,
                    month: e.data.month,
                    public_id: e.data.public_id,
                    previous_reading: e.data.previous_reading,
                    by: location.state?.payload?.by,
                    public_id2: location.state?.payload?.public_id,
                    name: location.state?.payload?.name,
                }
                const billUpData = {
                    year: e.data.year,
                    month: e.data.month,
                    public_id: e.data.public_id,
                    previous_reading: e.data.previous_reading,
                    by: location.state?.payload?.by,
                    public_id2: location.state?.payload?.public_id,
                    name: location.state?.payload?.name,
                    remark: e.data.remark,
                    serial_number: e.data.serial_number,
                    location_id: e.data?.location_pk,
                    zone_id: e.data?.zone_id,
                }
                return (
                    <div className="d-flex gap-1">
                        {e.data.caution_status === 'lock' ? (
                            <button
                                className="btn btn-outline-success"
                                onClick={() => handleShow(lockPayload)}
                                title="Unlock this customer"
                            >
                                <FaLockOpen/>
                            </button>
                        ) : (
                            <Link
                                className="btn btn-outline-danger disabled"
                                to="#"
                                title="Locked"
                                onClick={(ev) => ev.preventDefault()}
                            >
                                <FaLock/>
                            </Link>
                        )}
                        <Link
                            className="btn btn-outline-info"
                            to="/add-bill"
                            state={{ data: billUpData }}
                            title="Bill up this customer"
                        >
                            <FaArrowUp/>
                        </Link>
                    </div>
                )
            },
        },
        {
            headerName: "Status",
            field: "status",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Rate",
            field: "rate_name",
            sortable: true,
            filter: 'agSetColumnFilter',
        },
        {
            headerName: "Village",
            field: "village_name",
            sortable: true,
            filter: 'agSetColumnFilter',
        },
        {
            headerName: "Zone",
            field: "zone_name",
            sortable: true,
            filter: 'agSetColumnFilter',
        },
        {
            headerName: "Station",
            field: "station_name",
            sortable: true,
            filter: 'agSetColumnFilter',
        },
        {
            headerName: "Line",
            field: "line_name",
            sortable: true,
            filter: 'agSetColumnFilter',
        },
        {
            headerName: "MeterID",
            field: "serial_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Previous Reading",
            field: "previous_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "LockID",
            field: "lock_number",
            sortable: true,
            filter: true,
        }
    ], []);


    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
       <div className="row">
            <div className="col-md-6">
                <BackButton
                    to="/un-billed"
                    state={{ year: location.state?.payload?.year, month: location.state?.payload?.month, by: location.state?.payload?.by }}
                    label="Back to Summary"
                />
            </div>
            <div className="col-md-6">
                <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
            </div>
        </div>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            suppressMenuHide={true}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            rowData={unBilled}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}

                            ref={gridRef}
                            onGridReady={onGridReady}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
      <Modal show={show} onHide={handleClose} backdrop={isLocking ? 'static' : true}>
        <Modal.Header closeButton={!isLocking}>
          <Modal.Title>Lock Alert</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure?</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleClose} disabled={isLocking}>
            No
          </Button>
          <Button variant="danger" onClick={() => handleLock(data)} disabled={isLocking}>
            {isLocking ? 'Processing…' : 'Yes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default UnBilledDetail